import { assertNever, NormalizedNumber } from '@sparkdotfi/common-universal'
import { ExternalLinkIcon } from 'lucide-react'

import { NotWhitelistedDelegationWarning } from '../NotWhitelistedDelegationWarning'
import { ParticipateInDaoBanner } from '../participate-in-dao-banner/ParticipateInDaoBanner'
import { Token } from '@/domain/types/Token'
import { assets } from '@/ui/assets'
import { Button } from '@/ui/atoms/button/Button'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export type MyVotingPowerSidePanelProps =
  | {
      variant: 'no-voting-power'
    }
  | ({
      variant: 'voting-power'
    } & VotingPowerPanelProps)

export function MyVotingPowerSidePanel(props: MyVotingPowerSidePanelProps) {
  if (props.variant === 'no-voting-power') {
    return <ParticipateInDaoBanner />
  }

  if (props.variant === 'voting-power') {
    return <VotingPowerPanel {...props} />
  }

  assertNever(props)
}

interface VotingPowerPanelProps {
  spk: Token
  slashableStakedSpk: NormalizedNumber
  spkBalance: NormalizedNumber
  votingPower: NormalizedNumber
  isDelegating: boolean
  isDelegatingToNotWhitelisted: boolean
  openStakeDialog: () => void
  openDelegateDialog: () => void
}

function VotingPowerPanel({
  spk,
  votingPower,
  spkBalance,
  slashableStakedSpk,
  isDelegating,
  isDelegatingToNotWhitelisted,
  openStakeDialog,
  openDelegateDialog,
}: VotingPowerPanelProps) {
  return (
    <MainPanel>
      <Panel
        variant="quaternary"
        spacing="none"
        className="-mt-4 -mx-4 flex min-h-[164px] flex-col items-center justify-center gap-3 py-12 md:min-h-[174px]"
      >
        <div className="flex items-center gap-1">
          <div className="typography-label-2 text-tertiary">{isDelegating ? 'Delegated' : 'My'} voting power</div>
          {isDelegatingToNotWhitelisted ? (
            <NotWhitelistedDelegationWarning className="icon-size-sm" />
          ) : (
            <Info className="text-secondary">
              Voting power is calculated based on the number of SPK tokens you hold and/or stake. By delegating your
              voting power, you authorize the delegate to vote on Spark governance proposals with the power of your
              tokens on your behalf, while you retain full ownership of the tokens.
            </Info>
          )}
        </div>
        <div className="flex items-center gap-2">
          <img src={assets.governance.votingPowerIcon} alt="voting-power-icon" className="size-9" />
          <div
            className="typography-heading-2 text-primary-inverse"
            data-testid={testIds.spkGovernance.myVotingPowerSidePanel.votingPower}
          >
            {spk.format(votingPower, { style: 'compact' })}
          </div>
        </div>
      </Panel>
      <div className="flex flex-col gap-3 pt-6 pb-5">
        <SpkInfoRow
          label="Staked"
          spk={spk}
          amount={slashableStakedSpk}
          testId={testIds.spkGovernance.myVotingPowerSidePanel.stakedSpk}
        />
        <SpkInfoRow
          label="Owned"
          spk={spk}
          amount={spkBalance}
          testId={testIds.spkGovernance.myVotingPowerSidePanel.spkBalance}
        />
      </div>
      <div className="typography-body-3 border-[#373642] border-t pt-4 pb-5 text-tertiary">
        Participate in Spark Governance by delegating your voting power, or voting directly on proposals.
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ExternalLinkButton to={links.docs.governance} variant="secondary" size="l">
          Learn more <ExternalLinkIcon className="icon-size-xs shrink-0" />
        </ExternalLinkButton>
        <Button
          variant="primary"
          size="l"
          onClick={isDelegating ? openStakeDialog : openDelegateDialog}
          data-testid={
            isDelegating
              ? testIds.spkGovernance.myVotingPowerSidePanel.stakeSpkButton
              : testIds.spkGovernance.myVotingPowerSidePanel.delegateButton
          }
        >
          {isDelegating ? 'Stake SPK' : 'Delegate'}
        </Button>
      </div>
    </MainPanel>
  )
}

function MainPanel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <Panel variant="secondary" spacing="none" className={cn('rounded-md p-6', className)}>
      {children}
    </Panel>
  )
}

function SpkInfoRow({
  spk,
  amount,
  label,
  testId,
}: {
  spk: Token
  amount: NormalizedNumber
  label: string
  testId?: string
}) {
  return (
    <div className="typography-label-2 flex items-center justify-between text-primary-inverse" data-testid={testId}>
      <div className="flex items-center gap-1.5">{label}</div>
      <div className="flex items-center gap-2">
        <img src={spk.logo} alt={spk.symbol} className="size-3" />
        <div className="typography-label-2 text-primary-inverse">
          {spk.format(amount, { style: 'compact' })} {spk.symbol}
        </div>
      </div>
    </div>
  )
}
